import React, { useCallback, useEffect, useState } from 'react';
import { List, X } from '@phosphor-icons/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOnWhiteBackground, setIsOnWhiteBackground] = useState(false);
  const [isOnFeaturedHeader, setIsOnFeaturedHeader] = useState(false);
  const [isOnDarkFooterSection, setIsOnDarkFooterSection] = useState(false);
  const [isOnDarkSection, setIsOnDarkSection] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    viewPreference,
    isChatDrawerOpen
  } = usePortfolio();
  const isCaseStudyPage = location.pathname.includes('/case-study/');
  const isUSBankPage = location.pathname.includes('/case-study/us-bank');
  // Throttle function to limit how often the scroll handler fires
  const throttle = (func, delay) => {
    let lastCall = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return func(...args);
    };
  };
  // CRITICAL: Separate immediate scroll position check (no throttle)
  const checkScrollPosition = useCallback(() => {
    const scrollY = window.scrollY;
    const atTop = scrollY <= 5;
    setIsAtTop(atTop);
    setHasScrolled(!atTop);
    // Force black icons/logo on US Bank page even at top
    if (isUSBankPage) {
      setIsOnDarkSection(false);
      setIsOnWhiteBackground(true);
      setIsOnFeaturedHeader(false);
      setIsOnDarkFooterSection(false);
      return;
    }
    if (atTop) {
      setIsOnDarkSection(true);
      setIsOnWhiteBackground(false);
      setIsOnFeaturedHeader(false);
      setIsOnDarkFooterSection(false);
    }
  }, [isUSBankPage]);
  // Function to check background sections (throttled for performance)
  const checkBackgroundSections = useCallback(throttle(() => {
    const scrollY = window.scrollY;
    if (scrollY <= 5) {
      return;
    }
    // Force black icons/logo on US Bank page
    if (isUSBankPage) {
      setIsOnDarkSection(false);
      setIsOnWhiteBackground(true);
      setIsOnFeaturedHeader(false);
      setIsOnDarkFooterSection(false);
      return;
    }
    if (isCaseStudyPage) {
      const scrollPosition = window.scrollY;
      const heroSection = document.querySelector('.relative.w-full.h-screen');
      if (heroSection && scrollPosition < heroSection.offsetHeight - 70) {
        setIsOnDarkSection(true);
        setIsOnWhiteBackground(false);
        setIsOnFeaturedHeader(false);
        setIsOnDarkFooterSection(false);
        return;
      }
      const darkSections = document.querySelectorAll('.bg-black.text-white');
      const darkBlueSections = document.querySelectorAll('[style*="background-color: rgb(0, 30, 121)"], [style*="background-color: #001e79"]');
      let onDarkSection = false;
      darkSections.forEach(section => {
        if (!section) return;
        const rect = section.getBoundingClientRect();
        if (rect.top <= 70 && rect.bottom > 0) {
          onDarkSection = true;
        }
      });
      darkBlueSections.forEach(section => {
        if (!section) return;
        const rect = section.getBoundingClientRect();
        if (rect.top <= 70 && rect.bottom > 0) {
          onDarkSection = true;
        }
      });
      if (onDarkSection) {
        setIsOnDarkSection(true);
        setIsOnWhiteBackground(false);
        setIsOnFeaturedHeader(false);
        setIsOnDarkFooterSection(false);
        return;
      }
      setIsOnDarkSection(false);
      setIsOnWhiteBackground(true);
      setIsOnFeaturedHeader(false);
      setIsOnDarkFooterSection(false);
      return;
    }
    const darkSections = [document.getElementById('home'), document.querySelector('.min-h-\\[200vh\\]'), document.getElementById('about')];
    const whiteBackgroundSections = [document.getElementById('expertise'), document.getElementById('featured-work'), document.querySelector('.bg-white.py-20'), document.querySelector('.featured-header'), document.querySelector('.bg-white.py-16')];
    const darkFooterSection = document.querySelector('footer.bg-black.text-white') || document.querySelector('footer.bg-black') || document.querySelector('.bg-black.text-white.py-24');
    let onDarkSection = false;
    darkSections.forEach(section => {
      if (!section) return;
      const rect = section.getBoundingClientRect();
      if (rect.top <= 70 && rect.bottom > 0) {
        onDarkSection = true;
      }
    });
    let onDarkFooterSection = false;
    if (darkFooterSection) {
      const footerRect = darkFooterSection.getBoundingClientRect();
      onDarkFooterSection = footerRect.top <= 100;
    }
    let onFeaturedHeader = false;
    let onWhiteBackground = false;
    // Special handling for featured-work section
    const featuredWorkSection = document.getElementById('featured-work');
    const featuredHeader = document.querySelector('.featured-header');
    if (featuredWorkSection && featuredHeader) {
      const featuredHeaderRect = featuredHeader.getBoundingClientRect();
      // Only trigger white background when the featured header is at the top (within 10px)
      if (featuredHeaderRect.top <= 10 && featuredHeaderRect.bottom > 0) {
        onWhiteBackground = true;
        onFeaturedHeader = true;
      }
    }
    // Check other white sections (but skip featured-work since we handled it above)
    whiteBackgroundSections.forEach(section => {
      if (!section) return;
      if (section.id === 'featured-work') return; // Skip, already handled
      const rect = section.getBoundingClientRect();
      if (rect.top <= 70 && rect.bottom > 0) {
        onWhiteBackground = true;
        if (section.classList.contains('featured-header')) {
          onFeaturedHeader = true;
        }
      }
    });
    if (onDarkSection) {
      onFeaturedHeader = false;
      onWhiteBackground = false;
    } else if (onDarkFooterSection) {
      onFeaturedHeader = false;
      onWhiteBackground = false;
    }
    setIsOnDarkSection(onDarkSection);
    setIsOnDarkFooterSection(onDarkFooterSection);
    setIsOnFeaturedHeader(onFeaturedHeader);
    setIsOnWhiteBackground(onWhiteBackground);
  }, 50), [isCaseStudyPage]);
  const handleScroll = useCallback(() => {
    checkScrollPosition();
    checkBackgroundSections();
  }, [checkScrollPosition, checkBackgroundSections]);
  const handleLogoClick = e => {
    e.preventDefault();
    const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
    if (location.pathname === homePath) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate(homePath);
    }
  };
  useEffect(() => {
    checkScrollPosition();
    checkBackgroundSections();
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, checkScrollPosition, checkBackgroundSections]);
  useEffect(() => {
    const scrollY = window.scrollY;
    const atTop = scrollY <= 5;
    setIsAtTop(atTop);
    setIsOnDarkSection(atTop);
    setHasScrolled(!atTop);
    window.scrollTo(0, 0);
    setTimeout(() => {
      checkScrollPosition();
      checkBackgroundSections();
    }, 100);
  }, [location.pathname, checkScrollPosition, checkBackgroundSections]);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  const isAIMode = viewPreference === 'ai' && location.pathname === '/portfolio/ai';
  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Navigate to home based on current view preference
    navigate(homePath);
    toggleMenu();
  };
  const handleSectionClick = (sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    // If on case study page, navigate home first, then scroll
    if (isCaseStudyPage) {
      navigate(homePath);
      // Wait for navigation, then scroll
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 1;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const section = document.getElementById(sectionId);
      if (section) {
        const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 1;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    toggleMenu();
  };
  return <>
      {/* Header - adjusts width based on drawer state */}
      {/* FIXED: Hide on mobile when chat drawer is open (ChatInterface has its own mobile header) */}
      {/* FIXED: Adjust right position on ALL pages when chat is open (not just portfolio) */}
      <header className={`fixed top-0 left-0 z-[9999] p-6 flex justify-between items-start transition-all duration-300 ${isChatDrawerOpen ? 'hidden lg:flex' : 'flex'}`} style={{
      pointerEvents: 'none',
      right: isChatDrawerOpen ? '420px' : '0'
    }} data-id="element-300" data-mp="26h2wsl68giqe">
        <div className="relative flex items-center justify-center" style={{
        width: '36px',
        height: '36px',
        pointerEvents: 'auto'
      }} data-id="element-301" data-mp="2u4b57byyk7wn">
          {hasScrolled && !isAtTop && !isAIMode && <div className="absolute bg-white rounded-md header-logo-background" style={{
          width: '54px',
          height: '54px',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: '1',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          transition: 'opacity 0.3s ease'
        }} data-id="element-302" data-mp="1iq7ag3kjqfqj"></div>}
          <a href={homePath} className="logo-container relative z-10 flex items-center justify-center" onClick={handleLogoClick} style={{
          width: '36px',
          height: '36px',
          padding: '0',
          zIndex: '2'
        }} data-id="element-303" data-mp="1msqpkazckfcz">
            <img src={isAtTop && !isUSBankPage || isAIMode ? 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Logo_Container.png' : 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/blacklogom.png'} alt="Mateo Cohen Logo" style={{
            filter: 'none',
            visibility: 'visible',
            maxWidth: 'none',
            maxHeight: 'none',
            width: 'auto',
            height: '34px',
            objectFit: 'contain',
            opacity: '1',
            display: 'block',
            zIndex: '2',
            transition: 'all 0.3s ease'
          }} data-id="element-304" data-mp="1vs425cirpk1x" />
          </a>
        </div>

        <div className="relative flex items-center justify-center" style={{
        width: '36px',
        height: '36px',
        pointerEvents: 'auto'
      }} data-id="element-305" data-mp="28cj6la8epi3o">
          {hasScrolled && !isAtTop && !isAIMode && <div className="absolute bg-white rounded-md header-menu-background" style={{
          width: '54px',
          height: '54px',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: '1',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          transition: 'opacity 0.3s ease'
        }} data-id="element-306" data-mp="1zb35xu7e8ucn"></div>}
          <button onClick={toggleMenu} className="focus:outline-none relative z-10 flex items-center justify-center" aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} style={{
          padding: '0',
          width: '36px',
          height: '36px',
          zIndex: '2'
        }} data-id="element-307" data-mp="1ue2ybeuwbjjf">
            <List className="w-8 h-8" style={{
            color: isAtTop && !isUSBankPage || isAIMode ? 'white' : 'black',
            visibility: 'visible',
            opacity: '1',
            display: 'block',
            zIndex: '2',
            transition: 'color 0.3s ease'
          }} data-id="element-308" data-mp="3n70y7wwahefw" />
          </button>
        </div>
      </header>

      {isMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-95 z-[10000] flex items-center justify-center transition-all duration-300" data-id="element-309" data-mp="9t5zezccw7of">
          <button onClick={toggleMenu} className="absolute top-6 right-6 text-white focus:outline-none" aria-label="Close menu" data-id="element-310" data-mp="4x7fpp1z3duc">
            <X className="w-8 h-8" data-id="element-311" data-mp="3raywk5dsil7w" />
          </button>
          <nav className="text-center" data-id="element-312" data-mp="3ra5wtod90vz9">
            {isAIMode ? <ul className="space-y-8" data-id="element-313" data-mp="sl8jyls4c83y">
                <li data-id="element-314" data-mp="sm9ump582sl5">
                  <a href="/portfolio/traditional" className="text-2xl sm:text-3xl text-white tracking-wide hover:text-gray-400 transition-colors duration-300 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} onClick={handleHomeClick} data-id="element-315" data-mp="smrx43iha31i">
                    HOME
                  </a>
                </li>
                <li data-id="element-316" data-mp="155nmgdy75w32">
                  <a href="https://www.linkedin.com/in/mateocohen/" target="_blank" rel="noopener noreferrer" className="text-2xl sm:text-3xl text-white tracking-wide hover:text-gray-400 transition-colors duration-300 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} onClick={toggleMenu} data-id="element-317" data-mp="1a2x95lu7btn7">
                    LINKEDIN
                  </a>
                </li>
              </ul> : <ul className="space-y-8" data-id="element-318" data-mp="1suq3bjl0bkwi">
                <li data-id="element-319" data-mp="1xsrm03eiti7g">
                  <a href={homePath} className="text-2xl sm:text-3xl text-white tracking-wide hover:text-gray-400 transition-colors duration-300 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} onClick={handleHomeClick} data-id="element-320" data-mp="1xt9t5v2q1gu5">
                    HOME
                  </a>
                </li>
                <li data-id="element-321" data-mp="396yakm4l6ame">
                  <a href="#projects" className="text-2xl sm:text-3xl text-white tracking-wide hover:text-gray-400 transition-colors duration-300 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} onClick={handleSectionClick('projects')} data-id="element-322" data-mp="397gdu2po1p3t">
                    CASE STUDIES
                  </a>
                </li>
                <li data-id="element-323" data-mp="2phxn72dcy8x5">
                  <a href="#expertise" className="text-2xl sm:text-3xl text-white tracking-wide hover:text-gray-400 transition-colors duration-300 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} onClick={handleSectionClick('expertise')} data-id="element-324" data-mp="2lix5xoaj3ec9">
                    TOP SKILLS
                  </a>
                </li>
                <li data-id="element-325" data-mp="dbbu71hu707i">
                  <a href="#about" className="text-2xl sm:text-3xl text-white tracking-wide hover:text-gray-400 transition-colors duration-300 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} onClick={handleSectionClick('about')} data-id="element-326" data-mp="dbto3rh112c9">
                    ABOUT ME
                  </a>
                </li>
                <li data-id="element-327" data-mp="w10c9u0lam5g">
                  <a href="#contact" className="text-2xl sm:text-3xl text-white tracking-wide hover:text-gray-400 transition-colors duration-300 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} onClick={handleSectionClick('contact')} data-id="element-328" data-mp="w3mkzwz7v7p9">
                    CONTACT ME
                  </a>
                </li>
              </ul>}
          </nav>
        </div>}
    </>;
}